/**
 * TurnBanner — sticky full-width turn indicator that sits directly below the
 * TopBar. Always visible regardless of scroll position, so mobile players
 * never need to scroll up to figure out whose turn it is.
 *
 * Three visual states (label/colors swap, structure does NOT):
 *   - my turn → vivid green/blue, animated pulse, "당신의 차례입니다"
 *   - opponent's turn → muted gray, "{name}님의 차례를 기다리는 중"
 *   - game over → flat neutral, "게임이 종료되었습니다"
 *
 * Layout-stability contract (DO NOT regress — see TurnBanner.test.tsx and
 * docs/guides/coding-conventions/14-ui-layout-stability.md):
 *   - The wrapper element is ALWAYS rendered (no null return). Conditional
 *     visibility would collapse the box and shift the page below.
 *   - The hint row is ALWAYS rendered (uses NO-BREAK SPACE placeholder when
 *     no hint is supplied) so its line height is reserved.
 *   - min-height is set explicitly so wraps/hint length cannot change box
 *     height across state transitions.
 *   - Label uses white-space:nowrap + ellipsis to prevent long i18n strings
 *     from wrapping into a second line on narrow viewports.
 *
 * Accessibility:
 *   - Wrapper has role="status" + aria-live="polite". The hint slot uses
 *     visibility:hidden (not aria-hidden) to suppress the placeholder; this
 *     preserves layout AND keeps the placeholder out of the AT live-region
 *     announcement, which aria-hidden on a live-region descendant does not
 *     do reliably across NVDA/JAWS/VoiceOver.
 *
 * Exposes data-testid="my-turn-banner" with data-active="true|false" for
 * E2E, plus data-testid="my-turn-banner-hint" on the hint slot.
 */

interface TurnBannerProps {
  readonly isMyTurn: boolean;
  readonly isGameOver: boolean;
  readonly opponentName: string;
  readonly hint?: string;
}

// 64px buffers the worst-case content height (20px vertical padding + 16px
// label line + 12px hint line + 2px gap + 2px border) against user-agent
// font-size overrides.
const WRAPPER_MIN_HEIGHT = 64;
// U+00A0 NO-BREAK SPACE reserves the line-box height of the hint row even
// when no hint string is provided. A regular SPACE can be collapsed by some
// CSS normalization rules and silently break the height reservation.
const HINT_PLACEHOLDER = " ";

export function TurnBanner({
  isMyTurn,
  isGameOver,
  opponentName,
  hint,
}: TurnBannerProps) {
  const label = isGameOver
    ? "게임이 종료되었습니다"
    : isMyTurn
      ? "당신의 차례입니다"
      : `${opponentName}님의 차례를 기다리는 중`;

  const background = isGameOver
    ? "var(--cn-surface-tertiary, #2a2a2a)"
    : isMyTurn
      ? "var(--cn-accent-green-bg, #1a7a3b)"
      : "var(--cn-surface-tertiary, #2a2a2a)";
  const color = isGameOver
    ? "var(--cn-text-muted, #888)"
    : isMyTurn
      ? "var(--cn-accent-green, #4ade80)"
      : "var(--cn-text-muted, #888)";
  const borderColor = isGameOver
    ? "var(--cn-border-subtle, #333)"
    : isMyTurn
      ? "var(--cn-accent-green, #4ade80)"
      : "var(--cn-border-subtle, #333)";

  const hintText = !isGameOver && isMyTurn && hint ? hint : HINT_PLACEHOLDER;
  const hintVisible = !isGameOver && isMyTurn && Boolean(hint);

  return (
    <div
      data-testid="my-turn-banner"
      data-active={isMyTurn && !isGameOver ? "true" : "false"}
      role="status"
      aria-live="polite"
      style={{
        position: "sticky",
        top: 48,
        zIndex: 9,
        width: "100%",
        boxSizing: "border-box",
        padding: "10px 16px",
        minHeight: WRAPPER_MIN_HEIGHT,
        background,
        borderBottom: `2px solid ${borderColor}`,
        color,
        fontSize: 16,
        fontWeight: 700,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        animation:
          isMyTurn && !isGameOver
            ? "yinsh-turn-pulse 1.6s ease-in-out infinite"
            : "none",
      }}
    >
      <span
        style={{
          maxWidth: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </span>
      <span
        data-testid="my-turn-banner-hint"
        style={{
          fontSize: 12,
          fontWeight: 500,
          visibility: hintVisible ? "visible" : "hidden",
          maxWidth: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          opacity: 0.85,
        }}
      >
        {hintText}
      </span>
      <style>{`
        @keyframes yinsh-turn-pulse {
          0%, 100% { box-shadow: inset 0 0 0 0 var(--cn-accent-green, #4ade80); }
          50% { box-shadow: inset 0 0 0 3px var(--cn-accent-green, #4ade80); }
        }
      `}</style>
    </div>
  );
}
